import React, {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import classNames from 'classnames'
import useScrollStickyObserve, {
  IScrollStickyObserveParams,
  IScrollStickyState,
} from 'Components/hooks/use-scroll-sticky-observe'

export interface IProps extends IScrollStickyObserveParams {
  children(isFixed: boolean): ReactNode
  className?: string
  outerClassName?(isFixed: boolean): string
  outerStyle?(isFixed: boolean): CSSProperties | undefined
  innerClassName?(isFixed: boolean): string
  innerStyle?(isFixed: boolean): CSSProperties | undefined
}

const StickyWrapper = ({
  children,
  className,
  outerClassName,
  outerStyle,
  innerClassName,
  innerStyle,
  state,
  setState,
  canListenScroll,
  isCurrentFixed,
}: IProps) => {
  const {
    state: { isFixed, duration },
    setMarkNode,
  } = useScrollStickyObserve({
    state,
    setState,
    canListenScroll,
    isCurrentFixed,
  })

  const [isLeave, setIsLeave] = useState(isFixed)

  useEffect(() => {
    if (isFixed) {
      setIsLeave(true)
    }
  }, [isFixed])

  const curChildren = useMemo(() => {
    return children(Boolean(duration && isLeave))
  }, [duration, isLeave])

  return (
    <React.Fragment>
      <div className={classNames('tw-relative', className)}>
        <div className="mark" ref={setMarkNode} />
        {curChildren ? (
          <div
            className={outerClassName?.(Boolean(duration && isLeave))}
            style={{
              maxWidth: 1920,
              minWidth: duration && isLeave ? 1024 : undefined,
              ...outerStyle?.(Boolean(duration && isLeave)),
            }}
          >
            <div
              className={innerClassName?.(Boolean(duration && isLeave))}
              style={innerStyle?.(Boolean(duration && isLeave))}
            >
              <div
                onAnimationEnd={() => setIsLeave(isFixed)}
                className={classNames('tw-h-full', {
                  enter: isFixed,
                  leave: !isFixed,
                })}
              >
                {children(Boolean(duration && isLeave))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <style jsx>
        {`
          .mark {
            position: absolute;
            top: -30px;
            left: 0;
            right: 0;
            height: 1px;
          }
          .enter {
            animation: enter ${duration}ms ease;
          }
          @keyframes enter {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }

          .leave {
            animation: leave ${duration}ms ease;
          }
          @keyframes leave {
            from {
              transform: translateY(0);
            }
            to {
              transform: translateY(100%);
            }
          }
        `}
      </style>
    </React.Fragment>
  )
}

export default StickyWrapper
